import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthCtx {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshRole: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({
  user: null,
  session: null,
  isAdmin: false,
  loading: true,
  signOut: async () => {},
  refreshRole: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadRole = async (uid: string | null) => {
    if (!uid) { setIsAdmin(false); return; }
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", uid)
      .eq("role", "admin")
      .maybeSingle();
    // On transient errors (network drop, 5xx, timeout) keep the current
    // role state — a failed check must never read as "role revoked".
    if (error) return;
    setIsAdmin(!!data);
  };

  useEffect(() => {
    let initialised = false;
    const finishInit = () => {
      if (!initialised) {
        initialised = true;
        setLoading(false);
      }
    };

    // Set up listener FIRST so we don't miss events fired during init.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      setUser(sess?.user ?? null);
      // Defer Supabase calls inside the listener to avoid deadlocks
      setTimeout(() => {
        loadRole(sess?.user?.id ?? null).finally(finishInit);
      }, 0);
    });

    // Then check existing session as a safety net (in case no auth event fires).
    supabase.auth.getSession().then(({ data: { session: sess } }) => {
      setSession(sess);
      setUser(sess?.user ?? null);
      loadRole(sess?.user?.id ?? null).finally(finishInit);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  // Re-verify role when the tab regains focus or becomes visible again, so
  // a revoked admin loses UI access without needing to sign out and back in.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        loadRole(user?.id ?? null);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [user?.id]);


  const signOut = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
  };

  // Memoized so consumers (e.g. AdminRoute) can safely list it in effect
  // deps without re-firing on every AuthProvider render.
  const refreshRole = useCallback(async () => loadRole(user?.id ?? null), [user?.id]);

  return (
    <Ctx.Provider value={{ user, session, isAdmin, loading, signOut, refreshRole }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
